import * as React from 'react';
import { IOrthodonticReferral, IOrthodonticReferralToothDeviation } from '../../../../models/IOrthodonticReferral';
import { Select, TextInput } from 'soprasteria-ui-library';
import { useAuth } from '../../../../App';
import { OrthodonticReferralApi } from '../../../../api/OrthodonticReferralApi';
import { IListItem } from 'soprasteria-ui-library/dist/models/IFormInput';
import { ToothDeviationDropdown } from '../../../ToothDeviationDropdown/ToothDeviationDropdown';

interface IOrthodonticReferralTreatmentIssue {
    orthodonticReferral: IOrthodonticReferral;
}

export const OrthodonticReferralTreatmentIssue: React.FunctionComponent<IOrthodonticReferralTreatmentIssue> = ({ orthodonticReferral }) => {

    const auth = useAuth();
    const api = new OrthodonticReferralApi(auth);
    const [orthodonticReferralOralHygienes, setOrthodonticReferralOralHygienes] = React.useState<IListItem[]>();
    const [orthodonticReferralTreatmentNeeds, setOrthodonticReferralTreatmentNeeds] = React.useState<IListItem[]>();
    const [orthodonticReferralSagittals, setOrthodonticReferralSagittals] = React.useState<IListItem[]>();
    const [orthodonticReferralToothDeviations, setOrthodonticReferralToothDeviations] = React.useState<IOrthodonticReferralToothDeviation[]>();
    const [orthodonticReferralTransversalBites, setOrthodonticReferralTransversalBites] = React.useState<IListItem[]>();
    const [orthodonticReferralVerticallBites, setOrthodonticReferralVerticallBites] = React.useState<IListItem[]>();

    React.useEffect(() => {
        if (orthodonticReferral.canEdit && (orthodonticReferralOralHygienes === undefined || orthodonticReferralOralHygienes.length == 0)) {
            getOrthodonticReferralOralHygienes();
            getOrthodonticReferralTreatmentNeeds();
            getOrthodonticReferralSagittals();
            getToothDeviations();
            getTransversalBites();
            getVerticallBites();
        }
    }, [orthodonticReferral.canEdit])

    const getOrthodonticReferralOralHygienes = () => {
        api.getOrthodonticReferralOralHygienes((data) => {
            setOrthodonticReferralOralHygienes(data.map<IListItem>((item) => { return { text: item.description, value: item.orthodonticReferralOralHygieneId.toString() } }));
        });
    }

    const getOrthodonticReferralTreatmentNeeds = () => {
        api.getOrthodonticReferralTreatmentNeeds((data) => {
            setOrthodonticReferralTreatmentNeeds(data.map<IListItem>((item) => { return { text: item.description, value: item.orthodonticReferralTreatmentNeedsId.toString() } }));
        });
    }

    const getOrthodonticReferralSagittals = () => {
        api.getOrthodonticReferralSagittals((data) => {
            setOrthodonticReferralSagittals(data.map<IListItem>((item) => { return { text: item.description, value: item.orthodonticReferralSagittalId.toString() } }));
        });
    }

    const getToothDeviations = () => {
        api.getToothDeviations((data) => {
            setOrthodonticReferralToothDeviations(data);
        });
    }

    const getTransversalBites = () => {
        api.getTransversalBites((data) => {
            setOrthodonticReferralTransversalBites(data.map<IListItem>((item) => { return { text: item.description, value: item.orthodonticReferralTransversalBiteId.toString() } }));
        });
    }

    const getVerticallBites = () => {
        api.getVerticallBites((data) => {
            setOrthodonticReferralVerticallBites(data.map<IListItem>((item) => { return { text: item.description, value: item.orthodonticReferralVerticallBiteId.toString() } }));
        });
    }

    return (
        <>
            <div className={`row ${orthodonticReferral.canEdit ? "" : "readonly-less-margin"}`}>
                <h5 className="col-12">Frågeställning</h5>
                <div className="col-6">
                    {
                        orthodonticReferral.canEdit
                            ? <Select name="orthodonticReferralTreatmentNeedsId" label="Patientens subjektiva behandlingsbehov är" options={orthodonticReferralTreatmentNeeds ?? []} selectedValue={orthodonticReferral.orthodonticReferralTreatmentNeeds?.orthodonticReferralTreatmentNeedsId.toString()} placeholder="Välj" required isClearable requiredValidationMessage="Patientens subjektiva behandlingsbehov måste anges" inlineLabel />
                            : <TextInput name="readonlyOrthodonticReferralTreatmentNeedsId" label="Patientens subjektiva behandlingsbehov är" defaultValue={orthodonticReferral.orthodonticReferralTreatmentNeeds?.description} readonly inlineLabel />
                    }
                </div>
                <div className="col-6">
                    {
                        orthodonticReferral.canEdit
                            ? <Select name="orthodonticReferralOralHygieneId" label="Munhygienen är" options={orthodonticReferralOralHygienes ?? []} selectedValue={orthodonticReferral.orthodonticReferralOralHygiene?.orthodonticReferralOralHygieneId.toString()} placeholder="Välj" required isClearable requiredValidationMessage="Munhygienen måste anges" inlineLabel />
                            : <TextInput name="readonlyOrthodonticReferralOralHygieneId" label="Munhygienen är" defaultValue={orthodonticReferral.orthodonticReferralOralHygiene?.description} readonly inlineLabel />
                    }
                </div>
                <div className="col-6">
                    {
                        orthodonticReferral.canEdit
                            ? <Select name="orthodonticReferralSagittalId" label="Sagittalt" options={orthodonticReferralSagittals ?? []} placeholder="Välj" isClearable selectedValue={orthodonticReferral.orthodonticReferralSagittal?.orthodonticReferralSagittalId.toString()} inlineLabel />
                            : <TextInput name="readonlyOrthodonticReferralSagittalId" label="Sagittalt" defaultValue={orthodonticReferral.orthodonticReferralSagittal?.description ?? "Ej angett"} readonly inlineLabel />
                    }
                </div>
                <div className="col-6">
                    {
                        orthodonticReferral.canEdit
                            ? <Select name="orthodonticReferralVerticallBiteId" label="Vertikalt" options={orthodonticReferralVerticallBites ?? []} placeholder="Välj" isClearable selectedValue={orthodonticReferral.orthodonticReferralVerticallBite?.orthodonticReferralVerticallBiteId.toString()} inlineLabel />
                            : <TextInput name="readonlyOrthodonticReferralVerticallBiteId" label="Vertikalt" defaultValue={orthodonticReferral.orthodonticReferralVerticallBite?.description ?? "Ej angett"} readonly inlineLabel />
                    }
                </div>
                <div className="col-6">
                    {
                        orthodonticReferral.canEdit
                            ? <Select name="orthodonticReferralTransversalBiteId" label="Transversellt" options={orthodonticReferralTransversalBites ?? []} placeholder="Välj" isClearable selectedValue={orthodonticReferral.orthodonticReferralTransversalBite?.orthodonticReferralTransversalBiteId.toString()} inlineLabel />
                            : <TextInput name="readonlyOrthodonticReferralTransversalBiteId" label="Transversellt" defaultValue={orthodonticReferral.orthodonticReferralTransversalBite?.description ?? "Ej angett"} readonly inlineLabel />
                    }
                </div>
                <div className="col-6">
                    {
                        orthodonticReferral.canEdit
                            ? <ToothDeviationDropdown name="orthodonticReferralToothDeviationTypeIds" toothDeviations={orthodonticReferralToothDeviations ?? []} defaultValues={orthodonticReferral.orthodonticReferralToothDeviation?.map(a => a.orthodonticReferralToothDeviationTypeId.toString())} />
                            : <div className="form-group row">
                                <label className="col-4 col-form-label">Tandavvikelse finns angående:</label>
                                <div className="col-8">
                                    <ul className="list-no-styling document-list">
                                        {
                                            orthodonticReferral.orthodonticReferralToothDeviation && orthodonticReferral.orthodonticReferralToothDeviation?.length > 0 ?
                                                <>
                                                    {
                                                        orthodonticReferral.orthodonticReferralToothDeviation?.map((deviation, index) =>
                                                            <li key={index} >
                                                                <p>{deviation.description}</p>
                                                            </li>
                                                        )
                                                    }
                                                </>
                                                :
                                                <li>
                                                    <p>Ej angett</p>
                                                </li>
                                        }
                                    </ul>
                                </div>
                            </div>
                    }
                </div>
            </div>
            <hr />
        </>
    )
}